import React, { useState, createContext, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import './sass/theme-settings.scss';
import { getHexFromColorObject } from '../../../../utils/color';
import SmartScanPreview from './containers/SmartScanPreview/SmartScanPreview';
import ThemeSettingsForm from './containers/ThemeSettingsForm/ThemeSettingsForm';
import { makeFileUpload, makeGetFile } from '../../../../api/ajax';
import {
  selectors as customerConfigurationsSelectors,
  constants as customerConfigurationsConstants,
  actions as customerConfigurationsActions
} from '../../../../redux/customerConfigurations';
import { THEMES_STATE_KEYS } from '../../../../utils/themes';
import { SkeletonUI } from '../../../../components';

const getDefaultThemingValues = (themingSettings, localLogoUrl) => ({
  logoFile: localLogoUrl || null,
  headingColor: getHexFromColorObject(themingSettings?.headingColor) || '#003CA3',
  paragraphColor: getHexFromColorObject(themingSettings?.paragraphColor) || '#003CA3',
  successColor: getHexFromColorObject(themingSettings?.successColor) || '#00BD4B',
  errorColor: getHexFromColorObject(themingSettings?.errorColor) || '#D72356',
  warningColor: getHexFromColorObject(themingSettings?.warningColor) || '#FFBC1F',
  primaryButtonBackgroundColor: getHexFromColorObject(themingSettings?.primaryButtonBackgroundColor) || '#007BFF',
  primaryButtonTextColor: getHexFromColorObject(themingSettings?.primaryButtonTextColor) || '#FFF',
  secondaryButtonBackgroundColor: getHexFromColorObject(themingSettings?.secondaryButtonBackgroundColor) || '#E3E5E8',
  secondaryButtonTextColor: getHexFromColorObject(themingSettings?.secondaryButtonTextColor) || '#607289',
  error: null
});

const getDarkThemingValues = (themingSettings, localLogoUrl) => ({
  logoFile: localLogoUrl || null,
  headingColor: getHexFromColorObject(themingSettings?.headingColor) || '#FFFFFF',
  paragraphColor: getHexFromColorObject(themingSettings?.paragraphColor) || '#FFFFFF',
  successColor: getHexFromColorObject(themingSettings?.successColor) || '#00BE4A',
  errorColor: getHexFromColorObject(themingSettings?.errorColor) || '#DD3243',
  warningColor: getHexFromColorObject(themingSettings?.warningColor) || '#FFBC1F',
  primaryButtonBackgroundColor: getHexFromColorObject(themingSettings?.primaryButtonBackgroundColor) || '#007BFF',
  primaryButtonTextColor: getHexFromColorObject(themingSettings?.primaryButtonTextColor) || '#FFFFFF',
  secondaryButtonBackgroundColor: getHexFromColorObject(themingSettings?.secondaryButtonBackgroundColor) || '#333333',
  secondaryButtonTextColor: getHexFromColorObject(themingSettings?.secondaryButtonTextColor) || '#FFFFFF',
  error: null
});

/* \***********************! *\
   ThemeSettings Context
\* !************************! */
const ThemeSettingsContext = createContext(null);
const ThemeSettingsProvider = ThemeSettingsContext.Provider;
ThemeSettingsContext.displayName = 'ThemesSettingsContext';

export function useThemeContext() {
  const context = useContext(ThemeSettingsContext);

  if (!context) {
    const error = new Error();
    error.name = 'ThemeSettingsContext';
    error.message = 'To use useContext, you must be within the Provider scope';
    Error?.captureStackTrace && Error.captureStackTrace(error);
    throw error;
  }

  return context;
}

function ThemeSettings({ themeType }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState(null);

  const dispatch = useDispatch();
  const customerConfiguration = useSelector(customerConfigurationsSelectors.selectCustomerConfiguration);
  const defaultLogo = useSelector(customerConfigurationsSelectors.selectLocalLogoUrl);
  const darkLogo = useSelector(customerConfigurationsSelectors.selectDarkLogoUrl);

  const logoUrl = themeType === customerConfigurationsConstants.THEME.DEFAULT
    ? defaultLogo
    : darkLogo;

  const [themes, setThemes] = useState(null);

  useEffect(() => {
    if (customerConfiguration) {
      const theme = themeType === customerConfigurationsConstants.THEME.DEFAULT
        ? getDefaultThemingValues(customerConfiguration?.themes?.default, logoUrl)
        : getDarkThemingValues(customerConfiguration?.themes?.dark, logoUrl);

      setThemes(theme);
    }
  }, [customerConfiguration, themeType, logoUrl]);

  useEffect(() => {
    makeGetFile(`customerConfigurations/current/custom-logo?theme=${themeType}`).subscribe(({ response }) => {
      // When custom logo is configured, display it
      if (response && response.size > 0) {
        if (themeType === customerConfigurationsConstants.THEME.DEFAULT) {
          dispatch(customerConfigurationsActions.loadCurrentCustomerLogoSuccess(response));
        } else if (themeType === customerConfigurationsConstants.THEME.DARK) {
          dispatch(customerConfigurationsActions.loadCurrentCustomerDarkLogoSuccess(response));
        }
      }
    });
  }, [dispatch, themeType]);

  const setTheme = (key, value) => {
    setThemes({ ...themes, [key]: value });
  };

  const handleAddLogo = (logo) => {
    setTheme(THEMES_STATE_KEYS.logoFile, logo);

    if (themeType === customerConfigurationsConstants.THEME.DEFAULT) {
      dispatch(customerConfigurationsActions.loadCurrentCustomerLogoSuccess(logo));
    } else if (themeType === customerConfigurationsConstants.THEME.DARK) {
      dispatch(customerConfigurationsActions.loadCurrentCustomerDarkLogoSuccess(logo));
    }
  };

  async function objectUrlToBase64(objectUrl) {
    const response = await fetch(objectUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const handleSubmit = async (postData) => {
    const data = {};

    if (themeType === customerConfigurationsConstants.THEME.DEFAULT) {
      data.default = { ...postData };
    } else if (themeType === customerConfigurationsConstants.THEME.DARK) {
      data.dark = { ...postData };
    }

    setIsSubmitting(true);
    setShowSavedMessage(false);

    const file = await objectUrlToBase64(logoUrl);
    const fileKey = themeType === customerConfigurationsConstants.THEME.DEFAULT ? 'default.logoFile' : 'dark.logoFile';

    makeFileUpload('customerConfigurations/current/themingSettings', file, data, fileKey).subscribe(
      ({ response }) => {
        setIsSubmitting(false);
        setShowSavedMessage(true);
        setApiErrorMessage(null);
        Boolean(themes.error) && setTheme(THEMES_STATE_KEYS.errorObj, null);
        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
      },
      ({ response: { error } }) => {
        setIsSubmitting(false);
        Boolean(themes.error) && setTheme(THEMES_STATE_KEYS.errorObj, null);
        setApiErrorMessage(error);
      },
    );
  };

  if (themes === null) return (<SkeletonUI height="10rem" type="radius-horizontal" />);

  return (
    <ThemeSettingsProvider value={{ themes, setTheme, setThemes }}>
      <div className="theme-settings-wrapper">
        <div className="theme-settings-wrapper__col1">
          <ThemeSettingsForm
            logoUrl={logoUrl}
            handleAddLogo={handleAddLogo}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showSavedMessage={showSavedMessage}
            apiErrorMessage={apiErrorMessage}
          />
        </div>
        <div className="theme-settings-wrapper__col2">
          <SmartScanPreview
            logoUrl={logoUrl}
            themeType={themeType}
          />
        </div>
      </div>
    </ThemeSettingsProvider>
  );
}

ThemeSettings.propTypes = {
  themeType: PropTypes.number,
};

export default ThemeSettings;
